

import React, { createContext, useContext, useState, useEffect } from "react";
import { fetchServiceAndTaxSettings } from "../../../services/Settings/ServicesandTaxSettingsAndSessios/ServicesAndTaxApi";

const InvoiceContext = createContext();

export const InvoiceProvider = ({ children }) => {
  const [invoiceSettingsMap, setInvoiceSettingsMap] = useState({});
  const [defaultSettings, setDefaultSettings] = useState(null);

  const [printingMethod, setPrintingMethod] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);

  // ========================================================
  // 🟢 1) Load invoice settings from localStorage (initial)
  // ========================================================
  useEffect(() => {
    try {
      const saved = localStorage.getItem("invoiceSettingsMap");
      if (saved) {
        setInvoiceSettingsMap(JSON.parse(saved));
        console.log("📦 Restored invoice settings from localStorage");
      }
    } catch (err) {
      console.error("❌ Failed to load local invoice settings", err);
    }
  }, []);
  // ========================================================
  // 🟢 2) Save invoice settings to localStorage on changes
  // ========================================================
  useEffect(() => {
    localStorage.setItem("invoiceSettingsMap", JSON.stringify(invoiceSettingsMap));
  }, [invoiceSettingsMap]);

  // ========================================================
  // 🟢 Helper: Converts API numeric flags to boolean
  // ========================================================
  const flag = (val) => val === 1 || val === "1";

  // ========================================================
  // 🟢 3) Load default settings from API
  // ========================================================
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const s = await fetchServiceAndTaxSettings();
        console.log("📌 Default Settings From API:", s);

        if (!s) return;

        // Build the formatted default settings object
        setDefaultSettings({
          // -------------------
          // Service
          // -------------------
          service_required: flag(s.is_service_required),
          service_is_percentage: flag(s.service_is_percentage),
          service: flag(s.is_service_required)
            ? flag(s.service_is_percentage) ? 0 : Number(s.service)
            : Number(s.service),
          service_ratio:
            flag(s.is_service_required) && flag(s.service_is_percentage)
              ? Number(s.service)
              : 0,

          // -------------------
          // Tax
          // -------------------
          tax_required: flag(s.is_tax_required),
          tax_is_percentage: flag(s.tax_is_percentage),
          tax: flag(s.is_tax_required) && flag(s.tax_is_percentage)
            ? 0
            : Number(s.tax),
          tax_ratio:
            flag(s.is_tax_required) && flag(s.tax_is_percentage)
              ? Number(s.tax)
              : 0,

          // -------------------
          // Delivery
          // -------------------
          delivery_required: flag(s.is_delivery_required),
          delivery_is_percentage: flag(s.delivery_is_percentage),
          delivery: flag(s.is_delivery_required)
            ? flag(s.delivery_is_percentage) ? 0 : Number(s.delivery)
            : Number(s.delivery),
          delivery_ratio:
            flag(s.is_delivery_required) && flag(s.delivery_is_percentage)
              ? Number(s.delivery)
              : 0,

          // -------------------
          // Discount
          // -------------------
          discount_required: flag(s.is_discount_required),
          discount_is_percentage: flag(s.discount_is_percentage),
          discount: flag(s.is_discount_required)
            ? flag(s.discount_is_percentage) ? 0 : Number(s.discount)
            : Number(s.discount),
          discount_ratio:
            flag(s.is_discount_required) && flag(s.discount_is_percentage)
              ? Number(s.discount)
              : 0,

          // -------------------
          // Other invoice values
          // -------------------
          sub_total: 0,
          total_amount: 0,

          // -------------------
          // Printing settings
          // -------------------
          ctrl_p_print: s.ctrl_p_print,
          auto_preparation_print: s.auto_preparation_print
        });

      } catch (err) {
        console.error("❌ Failed to load default settings:", err);
      }
    };

    loadSettings();
  }, []);

  // ========================================================
  // 🟢 4) Return settings for order → (order settings OR default)
  // ========================================================
  const getInvoiceSettings = (orderId) => {
    return (
      invoiceSettingsMap[orderId] ||
      defaultSettings || {
        service: 0,
        service_ratio: 0,
        discount: 0,
        discount_ratio: 0,
        tax: 0,
        tax_ratio: 0,
        tax_is_percentage: false,
        tax_required: false,
        delivery: 0,
        delivery_ratio: 0,
        sub_total: 0,
        total_amount: 0,
      }
    );
  };

  // ========================================================
  // 🟢 5) Update invoice settings for specific order
  // ========================================================
  const setInvoiceSettings = (orderId, newSettings) => {
    setInvoiceSettingsMap((prev) => {
      const current = prev[orderId] || getInvoiceSettings(orderId);
      return {
        ...prev,
        [orderId]: { ...current, ...newSettings },
      };
    });
  };

  // ========================================================
  // 🟢 6) Calculate final total amount
  // ========================================================
  const calculateTotalAmount = (settings) => {
    const subTotal = Number(settings.sub_total || 0);

    // Helper: calculates fixed + percentage part
    const calcValue = (value, ratio) => {
      const v = Number(value || 0);
      const r = Number(ratio || 0);
      return v + (r / 100) * subTotal;
    };

    const service = calcValue(settings.service, settings.service_ratio);
    const tax = calcValue(settings.tax, settings.tax_ratio);
    const delivery = calcValue(settings.delivery, settings.delivery_ratio);
    const discount = calcValue(settings.discount, settings.discount_ratio);

    return subTotal + service + tax + delivery - discount;
  };

  // ========================================================
  // Printing settings
  // ========================================================
  const setInvoicePrinting = ({ method, auto }) => {
    setPrintingMethod(method || null);
    setAutoPrint(!!auto);
  };

  // ========================================================
  // Provider Value
  // ========================================================
  return (
    <InvoiceContext.Provider
      value={{
        getInvoiceSettings,
        setInvoiceSettings,
        calculateTotalAmount,
        defaultSettings,
        printingMethod,
        autoPrint,
        setInvoicePrinting,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
};

export const useInvoice = () => useContext(InvoiceContext);
